import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import type { BreakthroughCard } from '../lib/breakthrough';
import { PILLAR_CONFIG } from '../lib/breakthrough';

/**
 * Inline Breakthrough tip — shows a single card with expand/collapse.
 * Use this embedded in pages like team creation, submission editor, judging.
 */
export function BreakthroughTip({ card }: { card: BreakthroughCard }) {
  const [expanded, setExpanded] = useState(false);
  const pillar = PILLAR_CONFIG[card.pillar];

  return (
    <div className={`rounded-xl border p-3 ${pillar.bg} transition-all`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-start gap-2 text-left"
      >
        <span className="mt-0.5 text-base">{pillar.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase tracking-wider ${pillar.color}`}>
              {pillar.label}
            </span>
            <span className="text-xs text-ink-tertiary">·</span>
            <span className="text-xs text-ink-tertiary">{card.category}</span>
          </div>
          <p className="mt-0.5 text-sm font-medium text-ink">{card.title}</p>
          {expanded && (
            <p className="mt-1.5 text-sm leading-relaxed text-ink-secondary">{card.summary}</p>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-ink-tertiary" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-tertiary" />
        )}
      </button>
    </div>
  );
}

/**
 * Breakthrough tips section — shows multiple tips for a context.
 * Collapsible, with a link to the full Breakthrough reference page.
 */
export function BreakthroughTips({
  tips,
  title = 'Breakthrough Mindset',
}: {
  tips: BreakthroughCard[];
  title?: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? tips : tips.slice(0, 2);

  if (tips.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-tertiary">
          <Lightbulb className="h-3.5 w-3.5" />
          {title}
        </h3>
        <Link to="/breakthrough" className="text-xs text-ink-tertiary hover:text-ink-secondary">
          View all →
        </Link>
      </div>
      {visible.map((card) => (
        <BreakthroughTip key={card.id} card={card} />
      ))}
      {tips.length > 2 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-xs font-medium text-ink-tertiary hover:text-ink-secondary"
        >
          +{tips.length - 2} more tips
        </button>
      )}
    </div>
  );
}
